/**
 * Audience artifact page — /w/:slug/audiences/:id (flag-gated by RequireAudiences).
 *
 * Two panes: the audience DEFINITION on the left and the dedicated audience agent on the right — two
 * mutation paths onto one document. The definition renders through the visual {@link PredicateTree}
 * ({@link fromBackendDefinition} maps the stored contract plus the derived `rows` grounding into the
 * builder's shape) plus a "Show SQL" button that opens the whole-audience compiled SQL in a modal, fed
 * by `audience.ruleSql` (compile-only, re-run on each definition change). The Size leads the definition
 * pane as {@link AudienceSizeBar} — the count, its share as a bar, the refresh that (re)calculates it,
 * and Preview beside Calculate. Preview opens a bottom split (`audience.preview`) on desktop — or a
 * bottom sheet on mobile, so the definition/chat stack is not split a second time. The split is inside
 * the definition pane (chat stays on the right). The header carries a rename modal, Save, Activate, and —
 * for the creator or a workspace admin — Delete.
 *
 * DIRECT EDITING (ACT2-509) is a LOCAL DRAFT, not a write-through. The tree edits a copy held in
 * `draft`; nothing reaches the server until Save. That gives the page three states, and most of the
 * conditional logic below exists to keep them honest:
 *
 *   clean            — as before: Save promotes to 'saved', gated on a fresh count.
 *   dirty            — Save STORES the edits (status omitted, so a saved audience stays saved-but-dirty,
 *                      exactly as the agent's `update_audience_definition` leaves it) and Discard drops
 *                      them.
 *   dirty + agent    — the agent rewrote the STORED definition underneath an open draft. The draft is
 *                      NEVER overwritten; both versions coexist behind a Callout and the user chooses.
 *                      Only reachable on a DRAFT audience now — see below.
 *
 * THE AGENT PROPOSES, IT DOES NOT SAVE (ACT2-623). On a SAVED audience `update_audience_definition`
 * writes nothing and pushes nothing to RudderStack. It emits its version over SSE, this page adopts it
 * as the local `draft` (see `applyProposedDefinition`), and the human presses Save. So a chat message
 * can no longer move a live rETL source on its own. The proposal is memory-only: closing the page drops
 * it and the stored definition returns, which is the intended recovery model — the transcript still
 * holds the tool call, so the user can ask for it again. A DRAFT audience is unchanged: the agent
 * stores its edits directly, because a draft feeds nothing downstream.
 *
 * SIZE AND SAVE ARE DECOUPLED (ACT2-604). Both halves of the old coupling are gone:
 *
 *   · Calculate counts an OPEN DRAFT. `audience.count` takes the definition on screen and counts it
 *     without storing it, so an edit can be measured before anyone decides to keep it. The refresh used
 *     to be disabled mid-edit, because the count ran by id and would have measured the replaced rules.
 *   · Save COUNTS AFTER IT STORES. A save that moves the definition leaves the stored count describing
 *     rules that are gone, so `onSave` chains a count by id the moment the write lands. A failed count
 *     never rolls the save back — the edit is kept and the size bar carries the warehouse's message.
 *
 * The result is that the count on screen always describes the definition on screen, without the user
 * driving two controls in the right order to get there.
 *
 * Save is enabled only for a fresh, successful count of the CURRENT definition (`canSaveAudience`).
 * The client gate is UX only; the server (`AudienceCountRequiredError`) is the authority and its
 * refusal reason is surfaced verbatim.
 *
 * Activate opens the destination wizard (ACT2-512). Disabled for drafts and for a saved audience
 * whose definition has moved since the last count — save (and recount) first.
 *
 * Activate is HIDDEN rather than disabled-with-a-reason when `isAudiences` is false — the usual
 * rule against dead affordances applies to states the user can act their way out of, and "this
 * workspace has no audiences surface" is not one of them. Note that in practice `isAudiences` is
 * always true here: the route is behind `RequireAudiences` (App.tsx) and every `audience.*`
 * procedure is behind `requireAudiencesFlag`, so a denylisted workspace never reaches this page
 * at all — it is redirected, and a direct RPC (including delete) answers NOT_FOUND. The gate is NOT
 * scoped to Activate; it covers the whole vertical. The three `isAudiences &&` guards on this page
 * are belt-and-braces for the component rendered outside that route (tests, previews), which is why
 * they are kept and tested. The rest of the page (the definition, Size, Save, rename,
 * delete) is deliberately untouched by it, because building an audience is a warehouse-only
 * concern.
 */
import { keepPreviousData, skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Code2, Pencil, RotateCcw, Save, Send, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { AccountRepairBanner } from '@/_shared/components/AccountRepair/AccountRepairBanner';
import { REPAIR_AUDIENCE_FIRST } from '@/_shared/components/AccountRepair/copy';
import { NeedsRepairBadge } from '@/_shared/components/AccountRepair/NeedsRepairBadge';
import { RelinkNotice } from '@/_shared/components/AccountRepair/RelinkNotice';
import { ActionTooltip } from '@/_shared/components/ActionTooltip';
import { ConfirmDialog } from '@/_shared/components/ConfirmDialog';
import { KebabMenu, type KebabMenuAction } from '@/_shared/components/list/KebabMenu';
import { ListError } from '@/_shared/components/list/ListStates';
import { SqlModal } from '@/_shared/components/SqlModal';
import { Badge } from '@/_shared/components/ui/badge';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList } from '@/_shared/components/ui/breadcrumb';
import { Button, ButtonGroup } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/_shared/components/ui/collapsible';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/_shared/components/ui/resizable';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/_shared/components/ui/sheet';
import { Skeleton } from '@/_shared/components/ui/skeleton';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useIsMobile } from '@/_shared/hooks/use-mobile';
import { useAuth } from '@/_shared/hooks/useAuth';
import { useDebouncedValue } from '@/_shared/hooks/useDebouncedValue';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { usePageTitle } from '@/_shared/hooks/usePageTitle';
import { useRudderstackSignInAgain } from '@/_shared/hooks/useRudderstackSignInAgain';
import {
  useMissingRudderstackGrant,
  useRudderstackGrantsDecide,
  useSiblingActionsOff,
} from '@/_shared/hooks/useSiblingStatus';
import { ANALYTICS_EVENTS, trackClientEvent } from '@/_shared/lib/analytics';

import { AudienceActivationsLink } from '../components/AudienceActivationsLink';
import { PredicateTree } from '../components/audienceBuilder/PredicateTree';
import type { AudienceDefinition as BuilderDefinition } from '../components/audienceBuilder/types';
import { AudienceChatSidebar } from '../components/AudienceChatSidebar';
import { AudiencePreviewPanel } from '../components/AudiencePreviewPanel';
import { AudienceSizeBar, type PreviousCount } from '../components/AudienceSizeBar';
import { AudienceStatusBadge } from '../components/AudienceStatusBadge';
import { AudienceSyncStatus } from '../components/AudienceSyncStatus';
import { JoinedTablesPanel } from '../components/JoinedTablesPanel';
import { LazyAudienceActivationSheet } from '../components/LazyAudienceActivationSheet';
import { RenameAudienceDialog } from '../components/RenameAudienceDialog';
import { RowPickNote } from '../components/RowPickNote';
import { useAudienceChat } from '../hooks/useAudienceChat';
import {
  type AudienceRecord,
  countStateOf,
  isStale,
  needsCount,
  rsSyncStateOf,
  ruleCountOf,
  statusOf,
} from '../lib/audience-view';
import { fromBackendDefinition, toBackendDefinition } from '../lib/definition-adapter';
import { removedJoinedColumnsFrom } from '../lib/removedJoinedColumns';
import { type AudienceDefinition as BackendDefinition, canSaveAudience, type CountState } from '../types';

/** The "Show SQL" preview is compile-only and feeds nothing but the modal, so its RPC is debounced: a
 *  slider drag or fast typing settles before we re-compile, instead of one call per intermediate state. */
const SQL_DEBOUNCE_MS = 350;

/** Preview freshness is about the RULES, not object identity. Save and refetch mint a new definition
 *  object with the same content; `===` would keep the stale banner up after a successful Refresh+Save. */
function sameAudienceDefinition(a: BackendDefinition | null, b: BackendDefinition | null): boolean {
  if (a === b) return true;
  if (a == null || b == null) return false;
  return stableJson(a) === stableJson(b);
}

function stableJson(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableJson).join(',')}]`;
  const rec = value as Record<string, unknown>;
  return `{${Object.keys(rec)
    .sort()
    .filter((key) => key !== 'id' && rec[key] !== undefined)
    .map((key) => `${JSON.stringify(key)}:${stableJson(rec[key])}`)
    .join(',')}}`;
}

/**
 * What a count was computed for — the identity a displayed count is checked against before it is
 * allowed to describe the definition on screen.
 *
 * A STORED count is keyed by the definition hash the server stamped it with. A DRAFT count has no such
 * hash: the definition it measured was never sent to be stored, so the server never hashed it. The page
 * keys those by the edit revision it minted itself ({@link draftRevision}), which moves on every
 * keystroke — so a draft count expires the instant the draft it measured is edited again.
 *
 * One flat STRING rather than a tagged object, because this value sits in an effect's dependency array.
 * An object literal would carry a fresh identity into every render and re-run the effect forever.
 */
type CountKey = string;

interface PendingCountRecording {
  audienceId: string;
  forKey: CountKey;
  receipt: string;
}

const storedCountKey = (definitionHash: string): CountKey => `stored:${definitionHash}`;
const draftCountKey = (revision: number): CountKey => `draft:${revision}`;
const MEMBERSHIP_NEXT_STEP = 'Ask the assistant to fix this audience so it can sync.';

/** A count on screen: the numbers, when they were produced, and whether they describe the current rules. */
interface DisplayedCount {
  state: CountState;
  countedAt: string | null;
  stale: boolean;
}

/**
 * Which count the page shows, in priority order.
 *
 * The interesting case is the third. When an edit moves past the count that measured it, the display used
 * to fall back to the STORED count — a number that is equally out of date and OLDER, so the size appeared
 * to jump backwards and the measurement the user had just taken vanished. Between two counts that both
 * fail to describe the rules on screen, the more recent one is the better one to show, and the stale mark
 * says the same thing about either.
 *
 * Order matters between the second case and the third. Discard puts the stored definition back on screen,
 * and its stored count is then genuinely current — so "the stored count is for the stored definition"
 * has to beat "this session ran a count", or Discard would leave a stale number where a fresh one belongs.
 */
function resolveDisplayedCount(
  record: AudienceRecord | null,
  override: { state: CountState; forKey: CountKey; at: string; audienceId: string } | null,
  currentCountKey: CountKey | null,
  dirty: boolean,
  audienceId: string | null,
): DisplayedCount {
  const recorded: CountState = record !== null ? countStateOf(record) : { status: 'not-run' };
  // The row's own count describes the definition on screen: no draft is open, and the count is stamped
  // with the hash the row currently holds.
  const storedIsCurrent = !dirty && record !== null && !isStale(record);

  // 1 — a count of the definition ON SCREEN. The only fresh answer available while a draft is open.
  //
  // An ERROR override is the exception, and only when the row holds a real count for these very rules. A
  // Calculate the warehouse refused says nothing about the audience's SIZE, so letting it outrank that
  // count blanked a good number to "Unknown" and took the bar and the "counted N ago" line with it — for
  // the rest of the session, since nothing clears an override that still matches the current key. The
  // failure still reaches the user, because a count by id comes back as a REJECTION and `runCountFor`
  // toasts it.
  //
  // `recorded.status === 'counted'` is what keeps that narrow. With no recorded count there is nothing to
  // protect and the error is the only thing the bar can say — which is exactly the state a save leaves
  // behind when its chained count fails, and that one carries no toast of its own.
  const errorHidesAGoodCount = override?.state.status === 'error' && storedIsCurrent && recorded.status === 'counted';
  if (override !== null && currentCountKey !== null && override.forKey === currentCountKey && !errorHidesAGoodCount) {
    return { state: override.state, countedAt: override.at, stale: false };
  }
  // 2 — the stored count, when the stored definition is what is on screen. Where Discard lands.
  if (storedIsCurrent && record !== null) {
    return { state: recorded, countedAt: record.lastCountedAt, stale: false };
  }
  // 3 — the last count this session actually ran, for THIS audience. Stale, because the rules moved past
  // it. The id check is what stops a count that landed after a navigation being read as this audience's
  // size: the id-change block clears the override during render, and an in-flight count sets it again.
  if (override !== null && override.audienceId === audienceId && override.state.status === 'counted') {
    return { state: override.state, countedAt: override.at, stale: true };
  }
  // 4 — nothing measured here: the stored count, marked stale unless there is no count to mark.
  return { state: recorded, countedAt: record?.lastCountedAt ?? null, stale: recorded.status === 'counted' };
}

interface BoundRudderstackAccount {
  id: string;
  name?: string;
  type: string;
}

function AudienceRudderstackAccountStatus({
  accountId,
  account,
  loading,
  error,
}: {
  accountId: string | null;
  account?: BoundRudderstackAccount;
  loading: boolean;
  error: boolean;
}): React.ReactElement {
  let body: React.ReactNode;
  let badge: React.ReactNode;

  if (accountId === null) {
    badge = <Badge variant="outline">Not linked</Badge>;
    body = <p className="text-sm text-muted-foreground">This audience is not bound to a RudderStack rETL account.</p>;
  } else if (loading) {
    badge = <Badge variant="info">Checking</Badge>;
    body = (
      <div className="grid gap-2" aria-label="Loading bound RudderStack account">
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-3 w-64" />
      </div>
    );
  } else if (error) {
    badge = (
      <Badge variant="warning" bordered>
        Needs review
      </Badge>
    );
    body = (
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground">Bound account could not be verified</p>
        <p className="mt-0.5 truncate font-mono text-xs text-muted-foreground" title={accountId}>
          {accountId}
        </p>
      </div>
    );
  } else if (account) {
    badge = (
      <Badge variant="success" bordered>
        Linked
      </Badge>
    );
    body = (
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-foreground" title={account.name ?? account.id}>
          {account.name ?? 'Unnamed RudderStack account'}
        </p>
        <p className="mt-0.5 truncate font-mono text-xs text-muted-foreground" title={account.id}>
          {account.id}
        </p>
      </div>
    );
  } else {
    badge = (
      <Badge variant="warning" bordered>
        Needs review
      </Badge>
    );
    body = (
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground">Bound account not found</p>
        <p className="mt-0.5 truncate font-mono text-xs text-muted-foreground" title={accountId}>
          {accountId}
        </p>
      </div>
    );
  }

  return (
    <section
      aria-label="RudderStack rETL account"
      className="mb-4 rounded-xl border border-border bg-card p-3 shadow-sm"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="mb-1 text-xs font-medium tracking-wider text-muted-foreground uppercase">
            RudderStack rETL account
          </p>
          {body}
        </div>
        <div className="shrink-0">{badge}</div>
      </div>
    </section>
  );
}

export function AudiencePage(): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  const audienceId = id ?? null;
  const { wp, isAdmin, isAudiences, isAccountRepairEnabled } = useWorkspace();
  const { user } = useAuth();
  const orpc = useOrpc();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [activateOpen, setActivateOpen] = useState(false);
  // ACT2-1124: a disabled or gone sibling turns audience and activation actions off; the banner says why.
  const siblingActions = useSiblingActionsOff();
  // ACT2-1124 LLD §6 (F13a): in a sibling workspace RudderStack's grants decide audience writes, not Lookout's role.
  const grantsDecide = useRudderstackGrantsDecide();
  // ACT2-1124 LLD §6: where grants decide, the person's own sets turn off Activate, the delete of a synced audience,
  // and a save that pushes to RudderStack.
  const missingActivationGrant = useMissingRudderstackGrant('activations');
  const missingCreateDeleteGrant = useMissingRudderstackGrant('audiences_create_delete');
  const missingEditGrant = useMissingRudderstackGrant('audiences_edit');
  // ACT2-1124: an ended RudderStack sign-in offers "Sign in again" on the toast.
  const { toastIfReauth } = useRudderstackSignInAgain();

  const { data, isLoading, error } = useQuery(
    orpc.audience.get.queryOptions({ input: audienceId ? { id: audienceId } : skipToken }),
  );
  const record: AudienceRecord | null = data?.audience ?? null;
  // ACT2-1286: while its warehouse account needs repair a save still works (its push waits), and
  // activation create and edit are refused. Flag off: the server sends the off view and nothing changes.
  const repair = data?.accountRepair;
  const needsRepair = !!repair?.repairEnabled && (repair.state === 'needs_repair' || repair.state === 'unassigned');
  const rsAccountQuery = useQuery({
    ...orpc.audience.listRudderstackWarehouseAccounts.queryOptions({
      input: audienceId ? { id: audienceId } : skipToken,
    }),
    enabled: record?.rsAccountId != null,
    staleTime: 0,
    refetchOnMount: 'always',
  });
  const boundRsAccount =
    record?.rsAccountId != null && rsAccountQuery.data?.account?.id === record.rsAccountId
      ? rsAccountQuery.data.account
      : undefined;
  usePageTitle(record?.title ?? 'Audience');

  // Live count override for a count run on THIS page — cleared when the audience id changes, and
  // (via `forKey`) ignored once the definition has moved past what it was computed for, so a
  // stale refetched record can't hide behind a now-irrelevant fresh-looking override (see
  // `effectiveCountState`/`effectiveStale` below).
  //
  // This is also where a DRAFT count lives, and the only place it lives (ACT2-604): the server counts an
  // unsaved definition but records nothing for it, so nothing on the record can carry the answer.
  const [countOverride, setCountOverride] = useState<{
    state: CountState;
    forKey: CountKey;
    at: string;
    /** The audience the count measured. The id-change block clears the override during render, but an
     *  in-flight count sets it again afterwards — this page stays mounted across an id change. */
    audienceId: string;
    /**
     * The warehouse answered both counts but REFUSED the statement this audience publishes (ACT2-795)
     * — a metric view projected bare is the case that shipped. Deliberately a sibling of `state`
     * rather than a field inside `CountState`: the size is genuinely known and every consumer of
     * `CountState` (`canSaveAudience`, the delta, the bar) must go on reading it as `counted`. This
     * only adds a warning to render beside it. The warehouse's own text only (ACT2-797).
     */
    membershipDriverMessage?: string;
  } | null>(null);
  const [pendingCountRecording, setPendingCountRecording] = useState<PendingCountRecording | null>(null);

  // The size as it read BEFORE the most recent count — the "was" half of the size bar's delta.
  // Session-only by design (ACT2-600): it answers "what did the edit I just made do", a question about
  // this editing session, and a stored previous count would need a column to answer a question nobody
  // asks after a reload.
  //
  // Watched off the RECORD rather than captured when Calculate is clicked, because the button is not the
  // only thing that counts: the agent runs `count_audience` too, and `useAudienceChat` invalidates this
  // query on every turn boundary — so the record is where BOTH paths arrive. Capturing at the click gave
  // the delta to one of them and silently withheld it from the other.
  //
  // NO key of its own. `effectiveStale` already answers "does the number on screen describe the rules on
  // screen", and the chip is gated on it — a second key only gave the pair two ways to disagree, and it
  // did: Save re-stamped the override and left this half on the draft key, which silently ate the chip.
  const [previousCount, setPreviousCount] = useState<PreviousCount | null>(null);

  // The count this page has already seen. Opening an audience that ALREADY has a count is not a change,
  // so the first sighting only seeds this — a delta starts from the second count onwards.
  // Carries the audience id so a switch reads as a first sighting inside the effect. A ref cannot be
  // reset during render, and the id-change block below is render-time.
  const seenCount = useRef<{ audienceId: string; members: number } | null>(null);

  // ── Local edit draft ──
  // The tree edits a LOCAL copy; nothing reaches the server until Save. `forkedFromHash` is the
  // definition hash the draft branched off, which is the only way to notice that the agent rewrote the
  // definition underneath an open draft (see `agentMovedUnderDraft`) — comparing the definitions
  // themselves would also flag the user's own edit.
  const [draft, setDraft] = useState<{
    definition: BackendDefinition;
    forkedFromHash: string;
    /** The server's hash of THIS definition, when the agent supplied it. A hand edit clears it by
     *  omission — the page cannot hash — and a proposal count naming a hash the draft no longer has is
     *  dropped rather than shown against the wrong rules. */
    definitionHash?: string;
  } | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewedDefinition, setPreviewedDefinition] = useState<BackendDefinition | null>(null);

  // Monotonic edit counter — the identity a DRAFT count is stamped with, since an unstored definition has
  // no server hash. Every tree edit bumps it, which is exactly what expires a draft count: the number was
  // measured at revision N and the rules on screen are now revision N+1.
  //
  // NEVER reset while the audience stays open, not even on Discard or Save. It only has to be unique
  // within this page's lifetime, and a counter that went backwards could match a stale override.
  const [draftRevision, setDraftRevision] = useState(0);
  const draftRef = useRef(draft);
  const draftRevisionRef = useRef(draftRevision);

  /**
   * The agent's latest PROPOSED definition, and the only thing rows are ever re-derived for (ACT2-623).
   *
   * A hand edit does not need a derivation and never triggers one, exactly as before this vertical
   * existed: the tree cannot add a leaf, so every node it edits already has a row, and a row keeps
   * describing its column through an operand change. A PROPOSAL can add a leaf, and a leaf with no row
   * renders bare — no value picker, no distribution — so that one case has to be derived.
   *
   * Held separately from `draft` because the two move independently: a hand edit on top of a proposal
   * replaces the draft, and the rows derived for that proposal stay correct underneath it.
   */
  const [proposedForRows, setProposedForRows] = useState<BackendDefinition | null>(null);

  // True from the moment Save is pressed until the row and its count have both settled.
  //
  // It exists for the RudderStack sync band, which compares `rsPushedDefinitionHash` against
  // `definitionHash`. Those two are legitimately out of step mid-flight — a status-omitted save moves the
  // definition and pushes nothing — so the band would announce "RudderStack copy is out of date" for one
  // round trip on every save, and then withdraw it. Held across the chained count rather than tied to a
  // mutation's own `isPending`, because the two calls are one user action and a gap between them would
  // flash the band anyway.
  const [saveSettling, setSaveSettling] = useState(false);

  const [prevAudienceId, setPrevAudienceId] = useState(audienceId);
  if (audienceId !== prevAudienceId) {
    setPrevAudienceId(audienceId);
    setCountOverride(null);
    setPendingCountRecording(null);
    setPreviousCount(null);
    setSaveSettling(false);
    // Navigating to another audience abandons the draft. It belongs to the audience it forked from and
    // applying it to a different one would silently overwrite that audience's rules.
    setDraft(null);
    setPreviewOpen(false);
    setPreviewedDefinition(null);
    setProposedForRows(null);
  }

  /**
   * The agent PROPOSED a definition for a saved audience (ACT2-623). Nothing was stored, so this is the
   * only place the proposal exists: it becomes the page's local draft, and Discard and Save then behave
   * exactly as they do for a hand edit.
   *
   * It REPLACES an open draft rather than coexisting with one. That is the deliberate difference from
   * `agentMovedUnderDraft` below, which guards against a background write the user did not ask for. A
   * proposal is a direct answer to the message they just sent, it is visible, and Discard reverts it.
   *
   * The fork hash follows `onDefinitionChange`: stamped once, on the first edit, so it keeps tracking
   * the STORED definition the draft branched from.
   */
  const applyProposedDefinition = useCallback(
    (definition: unknown, definitionHash: string) => {
      if (!record) return;
      const current = draftRef.current;
      // Identical to the stored definition: there is nothing to propose, so the page goes back to
      // showing the stored rules. With nothing open that is a plain no-op — the prompt tells the agent
      // to echo ids verbatim, so an unchanged re-send is routine. With something open it is a REVERT:
      // adopting it instead would leave the page dirty with content equal to the row, where Save writes
      // nothing and a count records nothing (review #6). Same end state as pressing Discard.
      if (definitionHash === record.definitionHash) {
        if (current !== null) {
          draftRef.current = null;
          setDraft(null);
          setProposedForRows(null);
          setCountOverride(null);
          setPreviousCount(null);
          seenCount.current = null;
        }
        return;
      }
      // Already showing this exact proposal — a second emit must not bump the revision and expire
      // a count that still describes what's on screen.
      if (current?.definitionHash === definitionHash) return;
      // The same guard for a HAND edit, which carries no hash to compare. The agent is seeded with the
      // page's draft, so re-sending it unchanged is routine — and adopting it bumped the revision,
      // expired a count that still described the rules on screen, and refetched the rows for nothing.
      // Stamp the server's hash instead, so the next count can match by hash.
      if (
        current !== null &&
        current.definitionHash === undefined &&
        sameAudienceDefinition(current.definition, definition as BackendDefinition)
      ) {
        const stamped = { ...current, definitionHash };
        draftRef.current = stamped;
        setDraft(stamped);
        return;
      }
      const next = {
        definition: definition as BackendDefinition,
        forkedFromHash: current?.forkedFromHash ?? record.definitionHash,
        definitionHash,
      };
      // Written here, not only in the effect, so a second emit in the same turn sees the hash.
      draftRef.current = next;
      setDraft(next);
      // The one thing rows are re-derived for. A proposal can ADD a leaf, and a leaf with no row is a
      // rule the customer cannot edit.
      setProposedForRows(definition as BackendDefinition);
      // Expires any draft count on screen — it measured the rules as they were before this proposal.
      setDraftRevision((n) => n + 1);
    },
    [record],
  );

  /**
   * What the size bar is currently describing, readable from an event handler.
   *
   * A proposal and its count arrive as two SSE events, and the count carries the hash of the definition
   * it measured. Matching them needs the revision and hash as of the render the proposal produced —
   * which a callback closing over state would not have. Written in an effect, never during render.
   */
  const shownDraft = useRef<{ revision: number; hash: string | null }>({ revision: 0, hash: null });
  useEffect(() => {
    shownDraft.current = { revision: draftRevision, hash: draft?.definitionHash ?? null };
    draftRef.current = draft;
    draftRevisionRef.current = draftRevision;
  });

  /**
   * The agent counted the open proposal (ACT2-623). A proposal records nothing, so this override is the
   * only place that number can live — without it the size bar keeps reporting the stored count while the
   * agent quotes a different one in the same reply.
   *
   * The hash guard is what makes it safe: a count that names a definition the page is no longer showing
   * (the user edited on top of it, or discarded it) is DROPPED rather than shown against the wrong rules.
   */
  const applyProposalCount = useCallback(
    (count: {
      definitionHash: string;
      members: number;
      universe: number;
      percentage: number;
      revision?: number;
      membershipDriverMessage?: string;
    }) => {
      if (!audienceId) return;
      // Discard and Save clear the draft but NOT `draftRevision`, so a count seeded for a hand edit
      // still matches by revision and would put an override on a clean page — `resolveDisplayedCount`
      // case 3 then shows it as a stale size for rules nobody is looking at. `connected` is false for
      // the whole `sendMessage` round trip, which is exactly when Discard is reachable mid-turn.
      if (draftRef.current === null) return;
      const shown = shownDraft.current;
      // A proposal carries a hash the page stamped when it adopted the definition. A hand edit
      // clears that hash (the page cannot compute it), so match by the revision the page sent
      // with the draft instead — otherwise every count of a slider-edit is dropped.
      if (shown.hash !== null) {
        if (shown.hash !== count.definitionHash) return;
      } else if (count.revision === undefined || count.revision !== shown.revision) {
        return;
      }
      if (shown.hash === null) {
        // Stamp the server hash onto the draft so a later count of the same unedited rules can
        // match by hash. Does not bump the revision — the rules did not move.
        setDraft((current) =>
          current !== null && current.definitionHash === undefined
            ? { ...current, definitionHash: count.definitionHash }
            : current,
        );
      }
      setCountOverride({
        state: { status: 'counted', members: count.members, universe: count.universe, percentage: count.percentage },
        forKey: draftCountKey(shown.revision),
        at: new Date().toISOString(),
        audienceId,
        // Carried here for the same reason the Calculate path carries it: this override is the ONLY
        // home a proposal's count has, so a refusal dropped here is one the author never sees while
        // the agent is being told.
        membershipDriverMessage: count.membershipDriverMessage,
      });
    },
    [audienceId],
  );

  const chat = useAudienceChat(audienceId, {
    onProposal: applyProposedDefinition,
    onProposalCount: applyProposalCount,
    // Echoed back to the agent on the next message, so it builds on its own unsaved proposal.
    openDraft: draft?.definition ?? null,
    openDraftRevision: draft !== null ? draftRevision : undefined,
  });

  // What the page RENDERS: the draft while one is open, otherwise the stored definition. Everything
  // derived from the definition below reads this, so "Show SQL" and the condition count both describe
  // what is on screen rather than what is in the database.
  const effectiveDefinition = draft?.definition ?? record?.definition ?? null;
  const dirty = draft !== null;

  // ── The count on screen ──
  // What a count of the definition ON SCREEN would be stamped with. While dirty that is the draft's own
  // revision; otherwise it is the stored definition's hash.
  const currentCountKey: CountKey | null = dirty
    ? draftCountKey(draftRevision)
    : record
      ? storedCountKey(record.definitionHash)
      : null;

  const displayedCount = resolveDisplayedCount(record, countOverride, currentCountKey, dirty, audienceId);
  const effectiveCountState = displayedCount.state;
  const effectiveStale = displayedCount.stale;

  /**
   * The warehouse refused the statement this audience publishes, though it answered the counts
   * (ACT2-795). Rendered as a standing warning beside the size, because it stays true until the
   * definition changes — a toast would be gone before the user decides whether to Save.
   *
   * An edit moves `currentCountKey`, which retires the warning along with the number it came with —
   * the pair are one measurement and must expire together.
   *
   * Case 3 (a STALE override's numbers, still displayed) deliberately shows no warning: the refused
   * statement described a definition the tree no longer matches. That window cannot do harm —
   * `canSaveAudience` requires `!stale`, so Save is disabled exactly where the warning is absent,
   * and re-Calculating to re-enable Save re-raises it.
   *
   * Without a counted override for the definition on screen, the warning comes from the row
   * (ACT2-797): `recordCount` stores the refusal with the count, so it survives a reload and an
   * editor-agent recount. A FAILED recount leaves its error override in place and falls through to the
   * row too, so it does not retire the warning — only a count that ran clean does. The server refuses
   * a promotion on the same stored value, and Save is disabled while this shows.
   */
  const overrideCounted =
    countOverride !== null &&
    countOverride.audienceId === audienceId &&
    currentCountKey !== null &&
    countOverride.forKey === currentCountKey &&
    countOverride.state.status === 'counted';
  const membershipWarning = overrideCounted
    ? (countOverride.membershipDriverMessage ?? null)
    : !dirty && record && !needsCount(record)
      ? (record.lastCountMembershipError ?? null)
      : null;

  // The "was" half of the delta: the last count that ACTUALLY RAN, not the last one that was saved.
  //
  // Both halves of that sentence are load-bearing. Watching what is DISPLAYED rather than the record is
  // what covers both writers — the override the button sets, and the record an agent's `count_audience`
  // arrives on — and it keeps the button honest, because the record lags a click by a whole refetch.
  //
  // Counting only a FRESH display is what makes successive edits comparable (ACT2-604). Without that
  // guard, editing after a count drops the display back to the stored number, this effect records the
  // stored number as the latest thing seen, and the next count is measured against the last SAVE instead
  // of against the count before it. Every edit then read as a delta from the same distant baseline.
  // Skipping a stale display leaves the remembered number where it was, so counting, editing and counting
  // again reports exactly what that edit did.
  //
  // `currentCountKey` rather than a separately-derived one: a fresh display always describes the
  // definition on screen, because that is what `effectiveStale` being false means.
  const displayedMembers =
    !effectiveStale && effectiveCountState.status === 'counted' ? effectiveCountState.members : null;
  useEffect(() => {
    if (audienceId === null || displayedMembers === null) return;
    const seen = seenCount.current;
    seenCount.current = { audienceId, members: displayedMembers };
    // No comparison on a first sighting, on a number that did not move, or on the first count of a
    // DIFFERENT audience — the previous audience's size is not a previous size for this one.
    if (seen?.audienceId !== audienceId || seen.members === displayedMembers) return;
    setPreviousCount({ members: seen.members });
  }, [audienceId, displayedMembers]);

  // Debounced so a burst of edits (a slider drag, fast typing) coalesces into one compile. The tree and
  // count still read `effectiveDefinition` and update immediately — only this derived preview lags.
  const debouncedDefinition = useDebouncedValue(effectiveDefinition, SQL_DEBOUNCE_MS);
  const { data: sqlData, isFetching: sqlFetching } = useQuery(
    orpc.audience.ruleSql.queryOptions({
      input:
        record && debouncedDefinition
          ? { warehouseId: record.warehouseId, definition: debouncedDefinition }
          : skipToken,
    }),
  );
  // The header button stays put and DISABLES until the SQL matches the CURRENT definition — an edit still
  // debouncing, or the compile in flight. Disabling (not hiding) avoids the layout shift, and gating on the
  // current definition means the modal never opens onto stale SQL.
  const sqlPending = effectiveDefinition !== debouncedDefinition;
  const sqlReady = !sqlPending && !!sqlData?.membershipSql;
  const sqlDisabledReason = sqlReady
    ? null
    : sqlPending || sqlFetching
      ? 'Compiling SQL…'
      : 'SQL is not available for this definition';

  // The whole-audience compiled SQL, shown in a modal via the "Show SQL" header button.
  const [sqlOpen, setSqlOpen] = useState(false);

  const previewMutation = useMutation(orpc.audience.preview.mutationOptions());
  const previewAudienceIdRef = useRef(audienceId);

  useLayoutEffect(() => {
    previewAudienceIdRef.current = audienceId;
    previewMutation.reset();
    // Only the audience id is the event — `reset` is not a render input.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see above
  }, [audienceId]);

  const runPreview = (): void => {
    if (!record || !effectiveDefinition || audienceId == null) return;
    const definition = effectiveDefinition;
    const forAudience = audienceId;
    previewMutation.mutate(
      { id: audienceId, definition },
      {
        onSuccess: () => {
          if (previewAudienceIdRef.current !== forAudience) return;
          setPreviewedDefinition(definition);
        },
      },
    );
  };

  const onPreviewClick = (): void => {
    if (previewOpen) {
      setPreviewOpen(false);
      return;
    }
    setPreviewOpen(true);
    if (!sameAudienceDefinition(previewedDefinition, effectiveDefinition)) {
      runPreview();
    }
  };

  const previewStale =
    previewOpen && previewedDefinition !== null && !sameAudienceDefinition(previewedDefinition, effectiveDefinition);
  const previewError =
    previewMutation.isError && !previewMutation.isPending
      ? previewMutation.error instanceof Error
        ? previewMutation.error.message
        : 'Failed to preview audience members'
      : null;
  const previewResult = previewMutation.isPending ? null : (previewMutation.data ?? null);

  // ── Rows for an UNSAVED definition (ACT2-623) ──
  // `audience.get` derives rows for the STORED definition, and matching them by node id is enough for a
  // hand edit, which only ever changes a value or an operator on a node that already exists. It is NOT
  // enough for an agent PROPOSAL: the agent routinely ADDS a rule, and a leaf with no row renders bare —
  // no value picker, no control — which is the exact failure the grounding advisory exists to prevent.
  // Since the proposal is never stored, nothing re-derives them unless this does.
  //
  // Runs ONCE per proposal, not per edit. The input is a definition the server already validated on the
  // way out (`validateAudienceRevision`), so it cannot be refused here, and no debounce is needed — a
  // proposal arrives once a turn, not once a keystroke.
  const draftRowsQuery = useQuery({
    ...orpc.audience.rowsFor.queryOptions({
      input: audienceId && proposedForRows ? { id: audienceId, definition: proposedForRows } : skipToken,
    }),
    // A second proposal is a new key. Without this its rows blank for one round trip, the tree falls back
    // to the stored rows, and every control the first proposal added disappears and returns.
    placeholderData: keepPreviousData,
  });

  // Map the effective definition into the builder shape ONCE per definition/rows change — this page
  // re-renders often (chat SSE / count / rename ticks), and rebuilding the tree every render is wasted
  // work. `?? []` sits INSIDE the memo so an absent `rows` doesn't produce a fresh array identity on
  // every render and defeat it.
  //
  // Rows derived for a proposal win while one is open. `proposedForRows` gates the read as well as the
  // fetch: a disabled query keeps serving its last cached value, which after Discard would be rows for a
  // definition nobody is looking at. The stored rows are the floor — for a clean audience, and for a
  // draft the user typed themselves.
  const rows = (proposedForRows ? draftRowsQuery.data?.rows : undefined) ?? data?.rows;
  // A leaf the proposal ADDED has no stored row to reach for, so a failed derivation renders it as a
  // read-only sentence — no value picker, no distribution — while Save stays enabled. Silence is the
  // problem: every neighbouring fetch on this page reports its own failure, and this one looked like a
  // rule the agent simply could not ground.
  const draftRowsPending = proposedForRows !== null && draftRowsQuery.isPending;
  const draftRowsFailed = proposedForRows !== null && draftRowsQuery.isError;
  const builderView = useMemo(
    () => (effectiveDefinition ? fromBackendDefinition(effectiveDefinition, rows ?? []) : null),
    [effectiveDefinition, rows],
  );

  // Every tree edit lands here: translate back to the stored contract and hold it locally. The fork
  // hash is stamped once, on the FIRST edit — re-stamping on each keystroke would track the agent's
  // writes instead of detecting them.
  const onDefinitionChange = useCallback(
    (next: BuilderDefinition) => {
      if (!record) return;
      setDraft((current) => ({
        definition: toBackendDefinition(next),
        forkedFromHash: current?.forkedFromHash ?? record.definitionHash,
      }));
      // Expires any draft count already on screen: it measured the rules as they were one keystroke ago.
      setDraftRevision((current) => current + 1);
    },
    [record],
  );

  /**
   * Forget the delta baseline.
   *
   * The chip answers "what did the change I just made do". Save and Discard both END that change — one
   * keeps it, one abandons it — so neither leaves a question for the chip to answer, and the number that
   * follows is the audience's size rather than a move. Both halves have to go: clearing the state alone
   * leaves `seenCount` holding a number the next count would measure itself against, which is exactly how
   * a save WITHOUT a preceding Calculate still produced a chip while a save WITH one did not.
   */
  const resetDelta = useCallback(() => {
    setPreviousCount(null);
    seenCount.current = null;
  }, []);

  const discardDraft = useCallback(() => {
    draftRef.current = null;
    setDraft(null);
    setProposedForRows(null);
    resetDelta();
    // The override measured the draft that is being abandoned. Left in place it survives as case 3 of
    // `resolveDisplayedCount` whenever the STORED count is itself stale — so the size would show, marked
    // "Stale", a number no stored definition ever had, and hide the one the row actually carries.
    setCountOverride(null);
  }, [resetDelta]);

  // The agent rewrote the definition while a draft was open. We do NOT overwrite the draft — losing
  // typing to a background SSE tick is the worse failure — so the two versions coexist and the user
  // picks: keep editing (Save wins) or discard (the agent's version appears).
  const agentMovedUnderDraft = draft !== null && record !== null && record.definitionHash !== draft.forkedFromHash;

  const invalidateList = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: orpc.audience.list.key() });
  }, [queryClient, orpc]);

  /**
   * Refetch the audience and the list. RESOLVES once the detail refetch has landed, not when it is
   * merely queued — `saveSettling` hangs off that promise, and the row a save is waiting for arrives
   * only when the request finishes.
   */
  const invalidate = useCallback((): Promise<void> => {
    invalidateList();
    if (!audienceId) return Promise.resolve();
    return queryClient.invalidateQueries({
      queryKey: orpc.audience.get.queryOptions({ input: { id: audienceId } }).queryKey,
    });
  }, [audienceId, queryClient, orpc, invalidateList]);
  // A confirmed repair refreshes the audience, its list row and its sync status (ACT2-1286).
  const repairInvalidateKeys = useMemo(() => [orpc.audience.key()], [orpc]);
  // Dismissing the notice changes only the audience read: refetching the whole namespace would rerun
  // its warehouse row queries and RudderStack lookups for nothing.
  const dismissInvalidateKeys = useMemo(() => [orpc.audience.get.key()], [orpc]);

  // ── Count ──
  const countMutation = useMutation(orpc.audience.count.mutationOptions());
  const countRecordingMutation = useMutation(orpc.audience.recordCompletedCount.mutationOptions());
  const submitCountRecording = useCallback(
    (pending: PendingCountRecording, automatic: boolean) => {
      countRecordingMutation.mutate(
        { receipt: pending.receipt },
        {
          onSuccess: ({ recorded }) => {
            setPendingCountRecording((current) => (current?.receipt === pending.receipt ? null : current));
            if (recorded) {
              void invalidate();
            } else {
              setCountOverride((current) =>
                current?.audienceId === pending.audienceId && current.forKey === pending.forKey ? null : current,
              );
              void invalidate();
              toast.info('The audience changed. Calculate its size again.');
            }
          },
          onError: (error) => {
            // A busy automatic attempt leaves the receipt in place for the explicit retry control.
            if (!automatic) toast.error(error instanceof Error ? error.message : 'Could not record the count');
          },
        },
      );
    },
    [countRecordingMutation, invalidate],
  );
  const retryCountRecording = useCallback(() => {
    if (pendingCountRecording !== null) submitCountRecording(pendingCountRecording, false);
  }, [pendingCountRecording, submitCountRecording]);

  /**
   * Run one count and put the answer on screen, stamped with `forKey` so a later edit expires it.
   *
   * `definition` decides which of two genuinely different requests this is. Passing one sends the rules
   * in the body and the server records NOTHING — that is the draft count. Passing `null` counts the
   * stored definition by id and the server records the result against the row. A busy recording step
   * returns a receipt so the number stays visible while the user retries the local write.
   *
   * The caller states which it wants rather than this reading `dirty`. The post-save count runs inside
   * the very handler that clears the draft, so a `dirty` read here would see the flag as it was when
   * this callback was created and count the wrong thing.
   *
   * `silent` drops the failure TOAST, never the failure itself — the size bar renders the message either
   * way. It is for the count nobody clicked: chained onto a save, an error toast lands beside the save's
   * own success toast and reads as if the save had failed. `onSettled` fires either way, once the call
   * has finished.
   */
  const runCountFor = useCallback(
    (
      id: string,
      forKey: CountKey,
      definition: BackendDefinition | null,
      opts?: { silent?: boolean; recordOnly?: boolean; onSettled?: () => void },
    ) => {
      const silent = opts?.silent === true;
      // `recordOnly` runs the count for its SERVER effect and writes no override. The one caller is a
      // save that a newer proposal superseded: the row still needs a count for the rules it stored, but
      // that number describes rules the tree no longer shows. Written as an override it would miss
      // `currentCountKey` (a draft key, while dirty) and surface through `resolveDisplayedCount` case 3
      // as "the last count this session ran" — the proposal's size bar reporting the saved audience.
      const recordOnly = opts?.recordOnly === true;
      countMutation.mutate(
        { id, ...(definition !== null ? { definition } : {}) },
        {
          onSuccess: ({ result, recordingReceipt }) => {
            const at = new Date().toISOString();
            if (recordingReceipt !== undefined) {
              const pending = { audienceId: id, forKey, receipt: recordingReceipt };
              setPendingCountRecording(pending);
              // The number is already on screen. Retry only its local publication once, then leave
              // the existing button as a fallback if ownership remains busy.
              submitCountRecording(pending, true);
            } else if (definition === null) {
              setPendingCountRecording((current) => (current?.audienceId === id ? null : current));
            }
            if (recordOnly) return;
            if (!result.ok) {
              setCountOverride({ state: { status: 'error', error: result.error }, forKey, at, audienceId: id });
              return;
            }
            // No `previousCount` write here. The effect above derives the delta from what is DISPLAYED,
            // so this override feeds it directly — the same path an agent's `count_audience` takes.
            setCountOverride({
              state: {
                status: 'counted',
                members: result.members,
                universe: result.universe,
                percentage: result.percentage,
              },
              forKey,
              at,
              audienceId: id,
              membershipDriverMessage: result.membershipDriverMessage,
            });
          },
          onError: (err) => {
            const message = err instanceof Error ? err.message : 'Count failed';
            if (!recordOnly) {
              setCountOverride({
                state: { status: 'error', error: message },
                forKey,
                at: new Date().toISOString(),
                audienceId: id,
              });
            }
            if (!silent) toast.error(message);
          },
          onSettled: () => {
            // Only a stored count touches the row, so only that one refetches. A draft count records
            // nothing, and a refetch would bring back the same numbers and briefly repaint the size with
            // the count this one replaced.
            //
            // On SETTLE, not on success: a count chained onto a save is the only refetch that save gets
            // (see `onSave`), and a warehouse that refuses the count must still not leave the page holding
            // grounding derived for the definition the save replaced.
            if (definition !== null) {
              opts?.onSettled?.();
              return;
            }
            // Held until the refetch LANDS. A save is waiting on this row — the push that updates
            // `rs_pushed_definition_hash` runs after `saveAudience` reads its return value, so the
            // saved row a caller holds is a pre-push snapshot and the mirror's real state arrives
            // only here. Signalling at the request's start reopened the very window `saveSettling`
            // exists to close.
            void invalidate().finally(() => opts?.onSettled?.());
          },
        },
      );
    },
    [countMutation, invalidate, submitCountRecording],
  );

  // The size bar's refresh: count whatever is on screen. An open draft is sent in the body and stored
  // nowhere; a clean definition is counted by id and recorded (ACT2-604).
  const runCount = useCallback(() => {
    if (!audienceId || !record) return;
    // The key is rebuilt here rather than closed over. `currentCountKey` feeds the render path too, and
    // depending on it from a callback is what the React Compiler refuses to memoize.
    const forKey = draft !== null ? draftCountKey(draftRevision) : storedCountKey(record.definitionHash);
    runCountFor(audienceId, forKey, draft?.definition ?? null);
  }, [audienceId, record, draft, draftRevision, runCountFor]);

  // ── Save ──
  // ONE button, two jobs, and they must not be collapsed into one call:
  //
  //  · dirty  → persist the edited definition, status OMITTED. That is exactly what the agent's
  //             `update_audience_definition` does on a DRAFT audience: the row stays saved-but-dirty
  //             until it is re-counted. On a saved audience the agent only proposes, so this branch is
  //             also how an agent proposal is applied. Status-omitted writes skip the server's
  //             count-freshness precondition
  //             (`services/audience.ts` only gates `status: 'saved'`), which is what makes an edit
  //             storable at all — the stored count belongs to the definition being replaced.
  //  · clean  → promote to 'saved', the pre-existing behaviour, which DOES require a fresh count.
  //
  // Sending `status: 'saved'` with the edited definition would fail the precondition and, worse, would
  // silently promote a draft the moment its count happened to look fresh.
  const saveMutation = useMutation(orpc.audience.save.mutationOptions());
  // ACT2-1097: a save that stops sending joined columns is refused until the user confirms them, because a
  // destination that still maps one fails its whole sync. The prompt holds the names the server returned;
  // the ref carries the confirmed ones into the next save only.
  const [removalPrompt, setRemovalPrompt] = useState<string[] | null>(null);
  const confirmedRemovalRef = useRef<string[] | null>(null);
  // The single home for the save payload + failure toast. Save and Retry-sync differ only in WHICH
  // definition they send, whether they promote to `status: 'saved'`, and what they do on success — so both
  // route through here. Keeping the one mutate call here stops the two flows drifting apart (a new required
  // field, a `warehouseId` rename) with no compile error to catch it.
  const runSave = useCallback(
    (
      definition: BackendDefinition,
      promote: boolean,
      onSaved: (audience: AudienceRecord) => void,
      onFailed?: () => void,
    ): void => {
      if (!record) return;
      const confirmed = confirmedRemovalRef.current;
      confirmedRemovalRef.current = null;
      saveMutation.mutate(
        {
          id: record.id,
          title: record.title,
          definition,
          warehouseId: record.warehouseId,
          ...(promote ? { status: 'saved' as const } : {}),
          ...(confirmed !== null ? { confirmRemovedJoinedColumns: confirmed } : {}),
        },
        {
          onSuccess: (data) => onSaved(data.audience),
          // Surface the server's refusal reason verbatim (e.g. the count-required precondition).
          onError: (err) => {
            const removed = removedJoinedColumnsFrom(err);
            if (removed !== null) setRemovalPrompt(removed);
            else if (!toastIfReauth(err)) toast.error(err instanceof Error ? err.message : 'Failed to save audience');
            onFailed?.();
          },
        },
      );
    },
    [record, saveMutation, setRemovalPrompt, toastIfReauth],
  );

  const onSave = useCallback(() => {
    if (!record) return;
    // The key any count of the DRAFT would carry, read before the save clears it — the callback below runs
    // after `setDraft(null)`, where `dirty` is already false.
    const savedDraftKey = dirty ? draftCountKey(draftRevision) : null;
    const revisionAtClick = draftRevisionRef.current;
    setSaveSettling(true);
    // clean → promote to 'saved'; dirty → status omitted (just store the edit). See the block above.
    runSave(
      draft?.definition ?? record.definition,
      !dirty,
      (audience) => {
        // A proposal that arrived after this click is a NEWER edit than the one just stored, so the
        // draft stays on screen. Everything below this still runs: the row was written either way, and
        // skipping the chained count left it stored with a count measuring the rules it replaced.
        const supersededByNewerDraft = draftRevisionRef.current !== revisionAtClick;
        if (supersededByNewerDraft) {
          // The surviving draft branched off the definition this save just stored, so re-stamp its fork
          // hash. Left at the pre-save hash it trips `agentMovedUnderDraft`, whose Callout then reads
          // backwards: it offers to keep "your edits" (this is the assistant's newer proposal) and to
          // Discard to see "the assistant's version" (that is the row the user just saved themselves).
          setDraft((current) => (current === null ? null : { ...current, forkedFromHash: audience.definitionHash }));
        } else {
          // Clear the draft only after the server accepted it, so a rejected save leaves the edits on
          // screen to retry rather than reverting them to the stored version.
          setDraft(null);
          draftRef.current = null;
          resetDelta();
          // `setProposedForRows(null)` is deliberately NOT here. The seeded row below keeps the PRE-save
          // `rows`, so clearing the derived ones now strips the editor off every leaf the proposal added
          // until the chained count settles and its refetch lands — a whole warehouse round trip. It is
          // cleared in `onSettled` instead, beside `saveSettling`.
        }
        // Seed the saved row into the cache BEFORE invalidating, so `record` never lags `dirty`.
        //
        // Without this the page spends one full round-trip with `dirty` false and the PRE-SAVE row still in
        // hand. Every count key then points at the old definition: the size falls back to the stored number,
        // `isStale` compares the old hash to itself and reads FRESH, and the delta effect turns the drop
        // into a red "the audience shrank" chip measured against the count that was just saved.
        //
        // Merged onto the cached row, not swapped for it. `audience` is typed as the projection these
        // adapters read, so a straight swap would drop the fields outside it (`workspaceId`, `createdAt`,
        // `rsAccountId`). `rows` ride along untouched — they are keyed by node id, which an edit preserves,
        // and the refetch below replaces them.
        queryClient.setQueryData(orpc.audience.get.queryOptions({ input: { id: audience.id } }).queryKey, (old) =>
          old ? { ...old, audience: { ...old.audience, ...audience } } : old,
        );
        // ACT2-1286: the push was skipped, so never a plain "saved" that reads as synced.
        if (rsSyncStateOf(audience, isAccountRepairEnabled).status === 'needs-repair')
          toast.info('Saved in Lookout. It will sync after repair.');
        else toast.success(dirty ? 'Changes saved' : 'Audience saved');
        // A count of the draft is a count of the definition that was just stored — the same rules, now under
        // a server hash. Re-stamping it keeps that number on screen instead of dropping to the stale stored
        // one and back again while the count below is in flight.
        // Not re-stamped when a newer draft superseded this save: that override measures the draft still
        // on screen, and moving it onto the stored key would label it as the count of what was stored.
        if (savedDraftKey !== null && !supersededByNewerDraft) {
          setCountOverride((current) =>
            current !== null && current.forKey === savedDraftKey
              ? { ...current, forKey: storedCountKey(audience.definitionHash) }
              : current,
          );
        }
        // ACT2-604: a save that moved the definition leaves the recorded count measuring rules that are
        // gone, so count the definition that was just stored. By id, so the server RECORDS it — that is
        // what makes the audience promotable and activatable without a second trip to the refresh button.
        //
        // It runs even when the re-stamp above already put the right number on screen. The override is a
        // CLIENT fact; `saveAudience` refuses a promotion to `saved` on the row's own
        // `lastCountDefinitionHash`, so a count that was never recorded leaves the audience unpromotable.
        // Re-running the pair of COUNT(DISTINCT …) queries is the cost of counting and saving as two calls.
        //
        // Chained rather than folded into the save: the count is a warehouse round-trip that can fail, and
        // a failed one must never undo an accepted edit. The size bar carries the error instead, and this
        // count raises no toast of its own — nobody asked for it, and an error beside "Changes saved" reads
        // as though the save itself failed.
        //
        // Skipped when the stored count already matches, so promoting an unchanged clean draft to `saved`
        // does not re-run those queries for a number nobody moved.
        //
        // ONE `audience.get` AND ONE `audience.list` per Save. The row is already seeded above, and the
        // chained count refetches BOTH queries when it settles (`invalidate` starts with the list), so a
        // list invalidation here as well fetched it twice for one click — and a warehouse count is far
        // too slow for React Query to collapse the pair. The list therefore lags by the count's duration,
        // which nobody sees: it is not on screen during a Save.
        if (needsCount(audience)) {
          runCountFor(audience.id, storedCountKey(audience.definitionHash), null, {
            silent: true,
            // Superseded: record the count for the stored rules, show nothing. See `recordOnly`.
            recordOnly: supersededByNewerDraft,
            onSettled: () => {
              setSaveSettling(false);
              // Only once the refetch has replaced `rows` — see the note in the success branch above.
              if (!supersededByNewerDraft) setProposedForRows(null);
            },
          });
        } else {
          void invalidate().finally(() => {
            setSaveSettling(false);
            if (!supersededByNewerDraft) setProposedForRows(null);
          });
        }
      },
      () => setSaveSettling(false),
    );
  }, [
    record,
    draft,
    dirty,
    draftRevision,
    runSave,
    invalidate,
    runCountFor,
    resetDelta,
    queryClient,
    orpc,
    isAccountRepairEnabled,
  ]);

  // ── Retry sync ──
  // Re-push the mirror by re-saving the stored definition (status: 'saved', which `saveAudience`
  // re-pushes). It CANNOT reuse `onSave`'s toast: a push failure never fails the save (ACT2-506), so a
  // green "saved" toast would contradict a callout still reading "Sync to RudderStack failed". The push
  // outcome rides on the returned row's `rsPushError`, not on whether the save resolved — so read it off
  // the saved row: ONLY a genuinely `synced` mirror is a success; `error` is the recorded push failure; any
  // other residual (still out of date, or no mirror) means the re-push did not land and must not read as
  // success. The retry is offered only on a saved audience with a mirror and is disabled while
  // dirty/count-stale (`retryDisabledReason`), so re-saving `record.definition` here is always right.
  const onRetrySync = useCallback(() => {
    if (!record) return;
    runSave(record.definition, true, (audience) => {
      void invalidate();
      const rs = rsSyncStateOf(audience, isAccountRepairEnabled);
      if (rs.status === 'synced') toast.success('Synced to RudderStack');
      else if (rs.status === 'needs-repair') toast.info('Saved in Lookout. It will sync after repair.');
      else if (rs.status === 'error') toast.error('Sync to RudderStack failed');
      else toast.error('RudderStack sync did not complete');
    });
  }, [record, runSave, invalidate, isAccountRepairEnabled]);

  // ── Rename (modal, opened from the ⋯ context menu) ──
  const renameMutation = useMutation(orpc.audience.rename.mutationOptions());
  const [renameOpen, setRenameOpen] = useState(false);
  // Persists the new title. Resolves on success (the modal closes) and rejects on failure (it stays open
  // for a retry). A no-op rename (empty or unchanged) just closes without an API call.
  const onRename = useCallback(
    async (title: string) => {
      if (!record) return;
      const next = title.trim();
      if (next.length === 0 || next === record.title) return;
      try {
        await renameMutation.mutateAsync({ id: record.id, title: next });
        void invalidate();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to rename audience');
        throw err;
      }
    },
    [record, renameMutation, invalidate],
  );

  // ── Delete (creator or admin) ──
  const [deleteOpen, setDeleteOpen] = useState(false);
  const deleteMutation = useMutation(orpc.audience.delete.mutationOptions());
  const onDelete = useCallback(() => {
    if (!audienceId) return;
    deleteMutation.mutate(
      { id: audienceId },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: orpc.audience.list.key() });
          toast.success('Audience deleted');
          void navigate(wp('/audiences'));
        },
        onError: (err) => {
          if (!toastIfReauth(err)) toast.error(err instanceof Error ? err.message : 'Failed to delete audience');
        },
      },
    );
  }, [audienceId, deleteMutation, queryClient, orpc, navigate, wp, toastIfReauth]);

  if (error && !record) {
    return (
      <ListError
        title="Couldn't load audience"
        message={error instanceof Error ? error.message : 'This audience may have been deleted.'}
      />
    );
  }

  // While dirty, Save means "store these edits" and is always available; `canSaveAudience` gates only
  // the promote-to-saved job, which genuinely needs a fresh count.
  const countRecordingPending =
    pendingCountRecording?.audienceId === audienceId && pendingCountRecording.forKey === currentCountKey;
  const canSave =
    dirty ||
    (!countRecordingPending && canSaveAudience(effectiveCountState, effectiveStale) && membershipWarning === null);
  // ACT2-1124 LLD §6 (F13a): in a sibling workspace RudderStack's grants decide the delete of a SYNCED audience, and
  // the server answers 403 with the grant set. A never-synced one (no `rsSourceId`) has nothing in RudderStack to
  // grant on, so it keeps the creator-or-admin rule, as the server's `grantsDecide` does. The server's private-seat
  // rule needs no copy here: a challenge workspace performs zero RudderStack writes, so its audiences never sync.
  const grantsDecideDelete = grantsDecide && record?.rsSourceId != null;
  const canDelete = !!record && (grantsDecideDelete || isAdmin || record.createdBy === user?.id);
  // Shown, but off, for a person whose grants lack "Create and delete audiences": RudderStack would refuse it.
  const deleteGrantReason = grantsDecideDelete ? missingCreateDeleteGrant : null;
  // ACT2-1124 LLD §6: a save that makes or keeps an audience saved pushes it to RudderStack as the person, as the
  // server's push does. The first push creates the rETL source and needs "Create and delete audiences". A later push
  // edits that source and needs "Edit audiences". Null outside grantsDecide, as the hooks are.
  const pushGrantReason = record ? (record.rsSourceId != null ? missingEditGrant : missingCreateDeleteGrant) : null;

  // Discard and Save wait out a LIVE turn: Discard cannot reach the server-side proposal, so a later
  // emit would resurrect it, and Save mid-turn can store a title the agent's `rename_audience` already
  // moved. `connected` is what keeps the wait bounded — a dropped stream delivers no events, so there
  // is nothing to wait for, and the ephemeral proposal is never replayed on reconnect.
  const turnInFlight = chat.agentActive && chat.connected;
  // ...and a beat longer while the post-turn refetch lands, because `agentActive` clears BEFORE
  // `invalidateAudience` resolves and `runSave` sends `record.title` from whatever is in hand.
  //
  // The hook's own flag, NOT `audience.get`'s raw `isFetching`: that one is also true for a window-focus
  // refetch and for every unrelated invalidation, so an open draft lost Discard and Save for a beat
  // whenever the tab regained focus (review #3).
  const editControlsBusy = turnInFlight || chat.settlingAfterTurn;
  const editControlsReason = turnInFlight
    ? 'Wait until the assistant finishes this turn'
    : 'Refreshing the audience — one moment';

  const saved = record ? statusOf(record.status) === 'saved' : false;
  // Mirror Lookout Activations v2: Activate only when the audience is saved and the definition is
  // not ahead of its last count.
  // `!dirty` is its OWN term and must stay one. It used to ride on `effectiveStale`, which an open draft
  // forced true — but a counted draft is no longer stale (ACT2-604), and activating one would push the
  // STORED definition while a different one is on screen. Staleness is about the number; this is about
  // which rules RudderStack would receive.
  // ...and the same creator-or-admin rule Delete carries two lines up, because `enableActivation` now
  // enforces it server-side. Without this a member who did not create the audience gets a live button
  // and walks the entire wizard — accounts, audiences, mappings — before a 403 at Save. Disabled with
  // a reason rather than hidden, per the house rule.
  // ACT2-1124 LLD §6 (F13a): in a sibling workspace RudderStack's grants decide activation, so the role term drops out.
  const canActivateByRole =
    !!record && (grantsDecide ? missingActivationGrant === null : isAdmin || record.createdBy === user?.id);
  const canActivate =
    saved &&
    !dirty &&
    !effectiveStale &&
    !countRecordingPending &&
    canActivateByRole &&
    !siblingActions.off &&
    !needsRepair;
  // A blocked `saved` audience is blocked by one of the two terms above, and they are NOT interchangeable:
  // a counted draft is `dirty` but not `effectiveStale`. So test `dirty` first — falling through to
  // "Recalculate the size" would tell someone looking at a fresh number to calculate it again.
  const activateDisabledReason = siblingActions.off
    ? siblingActions.reason
    : needsRepair
      ? REPAIR_AUDIENCE_FIRST
      : !canActivateByRole
        ? (missingActivationGrant ?? 'Only the audience creator or a workspace admin can activate this audience.')
        : !saved
          ? 'A draft cannot be activated — save the audience first.'
          : dirty
            ? 'Save the latest definition before activating.'
            : countRecordingPending
              ? 'Record the count before activating.'
              : 'Recalculate the size before activating.';
  // Counted off the EFFECTIVE definition, not the stored one, so the badge describes what is on screen.
  const ruleCount = effectiveDefinition ? ruleCountOf(effectiveDefinition) : 0;

  // The header's ⋯ menu: Rename for everyone, Reset conversation gated on the agent having said anything, Delete
  // only for the creator / an admin (omitted entirely otherwise — no dead affordance).
  // ACT2-1124 LLD §5.9: Rename of a synced audience calls RudderStack, so a disabled or gone sibling disables it,
  // like Delete, with the banner's text as the reason. Reset conversation stays on, because it is local to Lookout.
  const menuActions: KebabMenuAction[] = [
    {
      label: 'Rename',
      icon: Pencil,
      disabled: siblingActions.off,
      disabledReason: siblingActions.reason,
      onSelect: () => setRenameOpen(true),
    },
    {
      label: 'Reset conversation',
      icon: RotateCcw,
      disabled: chat.messages.length === 0,
      disabledReason: 'No conversation to reset yet.',
      onSelect: chat.resetChat,
    },
    ...(canDelete
      ? [
          {
            label: 'Delete',
            icon: Trash2,
            destructive: true,
            disabled: siblingActions.off || deleteGrantReason !== null,
            disabledReason: siblingActions.off ? siblingActions.reason : (deleteGrantReason ?? ''),
            onSelect: () => setDeleteOpen(true),
          },
        ]
      : []),
  ];

  // Why a clean-DRAFT Save is disabled — a fresh count is the gate for promoting a draft to Active. The
  // dirty save is never gated (it only stores edits), and a clean SAVED audience shows no Save at all, so
  // neither needs a reason.
  const saveDisabledReason =
    dirty || saved
      ? undefined
      : countRecordingPending
        ? 'Wait for the count to be recorded before saving.'
        : effectiveCountState.status !== 'counted'
          ? 'Calculate the size to save.'
          : effectiveStale
            ? 'Recalculate the size to save.'
            : membershipWarning !== null
              ? MEMBERSHIP_NEXT_STEP
              : undefined;
  // The edit of a draft stays in Lookout, so "Update draft" needs no grant. Every other Save pushes.
  const saveGrantReason = saved || !dirty ? pushGrantReason : null;

  // No `calculateDisabledReason` any more (ACT2-604). The refresh used to be disabled mid-edit because
  // the count ran by id and would have measured the replaced rules; it now counts the draft itself, so
  // there is no state left in which the control is offered but must not be used.

  // When the displayed count was recorded — whichever source the ladder picked also supplies its time.
  const countedAt = displayedCount.countedAt;

  // The chip is shown only while the displayed count describes the definition ON SCREEN. `effectiveStale`
  // is that question already, and it is the only gate: it covers a definition the agent moved past the
  // count, and an uncounted draft, and using it rather than the override lets an agent-run count show a
  // chip at all. Save and Discard clear the baseline outright — see `resetDelta`.
  const previousForCurrent = !effectiveStale ? previousCount : null;

  // The sync retry re-saves the audience (status: 'saved'), which the server gates on a fresh count of the
  // current definition — mirror that gate: a dirty audience saves its draft first, and a clean one with a
  // stale/uncounted size must (re)calculate it before the re-push can go through.
  // ACT2-1124 LLD §5.9: the re-push calls RudderStack, so a disabled or gone sibling turns it off first, with the
  // banner's text as the reason. No other state makes the retry possible then. A missing grant set comes next (LLD §6):
  // the re-push is a save, so it needs the same set.
  const retryStateReason = editControlsBusy
    ? editControlsReason
    : countRecordingPending
      ? 'Record the count before retrying the sync.'
      : dirty
        ? 'Save your changes first, then retry the sync.'
        : effectiveCountState.status !== 'counted'
          ? 'Calculate the size before retrying the sync.'
          : effectiveStale
            ? 'Recalculate the size before retrying the sync.'
            : membershipWarning !== null
              ? MEMBERSHIP_NEXT_STEP
              : undefined;
  const retryDisabledReason = siblingActions.off ? siblingActions.reason : (pushGrantReason ?? retryStateReason);

  const definitionBlock =
    record && effectiveDefinition ? (
      <>
        {/* Withheld while a save is settling. The mirror's state is genuinely undecided between
            the write and the count that follows it, and a band that announces "out of date" for
            one round trip and then withdraws it is noise, not news. */}
        {repair?.repairEnabled && (repair.state !== 'ok' || repair.notice) && (
          <div className="mb-4 flex flex-col gap-2">
            <AccountRepairBanner
              itemRef={{ itemType: 'audience', id: record.id }}
              itemNoun="audience"
              view={repair}
              invalidateKeys={repairInvalidateKeys}
              detail="Your changes are saved in Lookout and will sync to RudderStack after repair. Until then it keeps syncing with its last pushed definition."
            />
            <RelinkNotice
              notice={repair.state === 'ok' ? repair.notice : null}
              itemRef={{ itemType: 'audience', id: record.id }}
              invalidateKeys={dismissInvalidateKeys}
              repairEnabled={repair.repairEnabled}
            />
          </div>
        )}
        {!saveSettling && (
          <AudienceSyncStatus
            state={rsSyncStateOf(record, isAccountRepairEnabled)}
            onRetry={onRetrySync}
            retrying={saveMutation.isPending}
            retryDisabledReason={retryDisabledReason}
          />
        )}

        <AudienceRudderstackAccountStatus
          accountId={record.rsAccountId ?? null}
          account={boundRsAccount}
          loading={record.rsAccountId != null && rsAccountQuery.isFetching}
          error={record.rsAccountId != null && rsAccountQuery.isError}
        />

        {agentMovedUnderDraft ? (
          <Callout variant="warning" className="mb-3">
            The assistant changed this audience while you were editing. Your edits are still here and Save keeps them;
            Discard shows the assistant&rsquo;s version instead.
          </Callout>
        ) : null}

        {/* Replaces the base-entity card (ACT2-600). The grain it used to state is now a comment
            in the generated SQL, where the reader who wants it is already looking. */}
        <AudienceSizeBar
          state={effectiveCountState}
          stale={effectiveStale}
          previous={previousForCurrent}
          countedAt={countedAt}
          onCalculate={runCount}
          pending={countMutation.isPending || countRecordingMutation.isPending}
          onPreview={onPreviewClick}
          previewOpen={previewOpen}
          previewPending={previewMutation.isPending}
        />

        {pendingCountRecording?.audienceId === audienceId ? (
          <Callout variant="warning" className="mt-3">
            The warehouse count is ready, but this audience was busy when Lookout tried to save it.
            <Button
              variant="secondary"
              size="sm"
              className="mt-2"
              loading={countRecordingMutation.isPending}
              onClick={retryCountRecording}
            >
              Retry saving count
            </Button>
          </Callout>
        ) : null}

        {/* The size above is real; the query that DELIVERS these people is not runnable. Nothing else
            on this page would say so — Preview is opt-in, and the sync only reports hours later
            (ACT2-795). */}
        {membershipWarning !== null ? (
          <Callout variant="warning" className="mt-3">
            This size is correct, but the warehouse refused the query that syncs these members. {MEMBERSHIP_NEXT_STEP}
            <Collapsible className="mt-2">
              <CollapsibleTrigger className="cursor-pointer text-xs">Warehouse message</CollapsibleTrigger>
              <CollapsibleContent className="mt-1 font-mono text-xs break-words">
                {membershipWarning}
              </CollapsibleContent>
            </Collapsible>
          </Callout>
        ) : null}

        <div className="mt-5 mb-2 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-foreground">Definition</h2>
            {ruleCount > 0 ? (
              <Badge variant="outline">
                {ruleCount} {ruleCount === 1 ? 'condition' : 'conditions'}
              </Badge>
            ) : null}
          </div>
          <ActionTooltip show={!sqlReady} message={sqlDisabledReason ?? ''} keepMounted>
            <Button
              variant="tertiary"
              size="sm"
              Icon={<Code2 />}
              label="Show SQL"
              onClick={() => {
                setSqlOpen(true);
                // `sqlReady` gates the button, so both values are present whenever this can run.
                // `ruleCount` is the same value the conditions badge renders, derived from the
                // definition rather than from the compile output.
                if (audienceId) trackClientEvent(ANALYTICS_EVENTS.audienceSqlViewed, { audienceId, ruleCount });
              }}
              disabled={!sqlReady}
            />
          </ActionTooltip>
        </div>

        {/* Editable: passing `onChange` is what switches every leaf from `ReadOnlyClause` to
            its grounded editor. `disabled` while a save is in flight so an edit cannot land
            on a definition that is already being replaced. */}
        {draftRowsFailed ? (
          <Callout variant="warning" className="mb-3">
            The editors for the assistant&rsquo;s new rules could not be loaded, so those rules show as text. Save still
            works. Reload the page to try again.
          </Callout>
        ) : null}
        {builderView && (
          <PredicateTree
            view={builderView}
            onChange={onDefinitionChange}
            onRawSqlViewed={() => {
              if (audienceId) trackClientEvent(ANALYTICS_EVENTS.audiencePredicateRawSqlViewed, { audienceId });
            }}
            // Disabled while the derivation is in flight as well: the tree would otherwise offer the
            // STORED rows' editors for a proposal whose own rows are still arriving.
            state={saveMutation.isPending || draftRowsPending ? 'disabled' : 'ready'}
            defaultInsightOpen
          />
        )}
        {/* Read-only (ACT2-783): the joins ride through the builder untouched, so this reads the same
            effective definition "Show SQL" compiles — a proposal that adds a join shows here at once. */}
        <JoinedTablesPanel
          joins={effectiveDefinition?.expression.joins}
          entity={effectiveDefinition?.expression.from}
        />
        <RowPickNote
          latestBy={effectiveDefinition?.expression.latestBy}
          rowKey={effectiveDefinition?.expression.rowKey}
        />
      </>
    ) : null;

  const previewPanel =
    record && effectiveDefinition ? (
      <AudiencePreviewPanel
        sourceName={effectiveDefinition.expression.from}
        loading={previewMutation.isPending}
        error={previewError}
        stale={previewStale}
        onRefresh={runPreview}
        onClose={() => setPreviewOpen(false)}
        result={previewResult}
        fillHeight
      />
    ) : null;

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
      {/* Header — chrome above the split: breadcrumb + title + status, and (right) Save, Activate and
          overflow. The Size is NOT here: it used to sit beside Save because it gates Save, and ACT2-600
          moved it to the head of the definition pane, where the count is the first thing read rather
          than a chip in a corner. `saveDisabledReason` is what now carries the gate across that gap. */}
      <header className="border-b border-border px-6 py-4">
        {/* `Breadcrumb` is the app's landmark for "where am I" and every detail page uses it, so this
            one does too rather than inventing a second idiom next to it.

            NO trailing `BreadcrumbSeparator`. It used to end with one and render "Audiences ›" — a
            chevron pointing at nothing, because a separator is a promise that another crumb follows.
            The page's own name is the `<h1>` directly below, so the alternative (a `BreadcrumbPage`
            restating it) would say the same words twice a line apart. One crumb, no promise. */}
        <Breadcrumb className="mb-4">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to={wp('/audiences')}>Audiences</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {isLoading && !record ? (
          <Skeleton className="h-8 w-64" />
        ) : record ? (
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            {/* The IDENTITY cluster: what this audience is called, what state it is in, and how far it
                reaches. Everything here reports; nothing here acts. The counterpart cluster on the right
                is the inverse — Activate, overflow — which is why the activations count lives here
                and not there, where it read as a button that was not one.

                `gap-3` rather than the title/badge `gap-2`: the count is a separate fact from the badge,
                not a second chip on it, and at the same gap the two read as one pair. */}
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex min-w-0 items-center gap-2">
                <h1 className="min-w-0 truncate text-lg font-semibold text-foreground">{record.title}</h1>
                <AudienceStatusBadge status={saved ? 'saved' : 'draft'} />
                <NeedsRepairBadge
                  state={repair?.repairEnabled ? repair.state : 'ok'}
                  withTooltip
                  className="shrink-0"
                />
              </div>
              {/* Gated on `rsSourceId`, not on `saved`: the id IS the evidence. An audience never pushed
                  to RudderStack has no source for a sync to read, so there is nothing to count — whereas
                  `saved` would also gate out a saved-then-edited audience whose existing syncs are still
                  running against the stored definition. Renders itself away at zero and while loading. */}
              {isAudiences && record.rsSourceId != null && <AudienceActivationsLink rsSourceId={record.rsSourceId} />}
            </div>

            {/* No Size control here — see the header comment above. Two size controls on one page would
                disagree the moment one of them lagged. */}
            <div className="flex shrink-0 flex-wrap items-center gap-2 md:justify-end">
              <div className="flex items-center gap-2">
                {dirty ? (
                  // Editing: Discard + Save as one segmented control. "Update draft" on a draft, "Save" on a
                  // saved (Active) audience — both just store the edits (never gated on a count). Both wait
                  // out a live turn and the refetch that follows it — see `editControlsBusy`.
                  <ActionTooltip
                    show={editControlsBusy || siblingActions.off || saveGrantReason !== null}
                    message={
                      editControlsBusy
                        ? editControlsReason
                        : siblingActions.off
                          ? siblingActions.reason
                          : (saveGrantReason ?? '')
                    }
                    keepMounted
                  >
                    <ButtonGroup>
                      <Button
                        variant="secondary"
                        size="md"
                        onClick={discardDraft}
                        disabled={saveMutation.isPending || editControlsBusy}
                      >
                        Discard
                      </Button>
                      <Button
                        variant="primary"
                        size="md"
                        onClick={onSave}
                        disabled={
                          saveMutation.isPending || editControlsBusy || siblingActions.off || saveGrantReason !== null
                        }
                        loading={saveMutation.isPending}
                        Icon={<Save />}
                      >
                        {saved ? 'Save' : 'Update draft'}
                      </Button>
                    </ButtonGroup>
                  </ActionTooltip>
                ) : !saved ? (
                  // A clean DRAFT: Save promotes it to Active, gated on a fresh count (disabled-with-reason).
                  // A clean SAVED (Active) audience shows NO save action — its definition is already stored,
                  // and a stale size is refreshed from the size bar in the definition pane, not from here.
                  <ActionTooltip
                    show={
                      editControlsBusy || saveDisabledReason != null || siblingActions.off || saveGrantReason !== null
                    }
                    message={
                      editControlsBusy
                        ? editControlsReason
                        : siblingActions.off
                          ? siblingActions.reason
                          : (saveGrantReason ?? saveDisabledReason ?? '')
                    }
                  >
                    <Button
                      variant="primary"
                      size="md"
                      onClick={onSave}
                      // The same wait the dirty pair takes. This branch WRITES: `runSave` sends the
                      // cached `record.title` and definition, so a click during a turn reverts an agent
                      // `rename_audience` and promotes on the pre-turn definition — the freshness gate
                      // accepts it, because that definition's hash is still the counted one.
                      disabled={
                        !canSave ||
                        saveMutation.isPending ||
                        editControlsBusy ||
                        siblingActions.off ||
                        saveGrantReason !== null
                      }
                      loading={saveMutation.isPending}
                      Icon={<Save />}
                    >
                      Save
                    </Button>
                  </ActionTooltip>
                ) : null}
                {isAudiences && (
                  <ActionTooltip show={!canActivate} message={activateDisabledReason}>
                    <Button
                      variant="secondary"
                      size="md"
                      disabled={!canActivate}
                      suffixIcon={<Send />}
                      onClick={() => setActivateOpen(true)}
                    >
                      Activate
                    </Button>
                  </ActionTooltip>
                )}
                <KebabMenu label="More actions" actions={menuActions} />
              </div>
            </div>
          </div>
        ) : null}
      </header>

      {/* Body — a resizable split between the read-only DEFINITION and the dedicated audience agent
          (chat is the sole mutation path). Horizontal on desktop, stacked (vertical) on mobile — one
          panel group that flips direction so there is a single chat instance either way. */}
      <ResizablePanelGroup
        direction={isMobile ? 'vertical' : 'horizontal'}
        autoSaveId="audience-split-layout"
        className="min-h-0 flex-1"
      >
        <ResizablePanel id="audience-definition" defaultSize="62%" minSize="38%">
          {record && effectiveDefinition ? (
            isMobile ? (
              <div className="h-full min-h-0 overflow-y-auto px-6 py-6">
                <div className="mx-auto w-full max-w-2xl">{definitionBlock}</div>
              </div>
            ) : (
              <ResizablePanelGroup direction="vertical" autoSaveId="audience-definition-preview" className="min-h-0">
                <ResizablePanel id="audience-definition-body" defaultSize={previewOpen ? '58%' : '100%'} minSize="30%">
                  <div className="h-full min-h-0 overflow-y-auto px-6 py-6">
                    <div className="mx-auto w-full max-w-2xl">{definitionBlock}</div>
                  </div>
                </ResizablePanel>
                {previewOpen ? (
                  <>
                    <ResizableHandle withHandle aria-label="Resize the audience preview" />
                    <ResizablePanel id="audience-preview" defaultSize="42%" minSize="20%">
                      <div className="flex h-full min-h-0 flex-col overflow-hidden border-t border-border">
                        {previewPanel}
                      </div>
                    </ResizablePanel>
                  </>
                ) : null}
              </ResizablePanelGroup>
            )
          ) : (
            <div className="flex h-full min-h-0 flex-col overflow-y-auto px-6 py-6">
              <div className="mx-auto w-full max-w-2xl">
                <div className="grid gap-3">
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-24 w-full" />
                </div>
              </div>
            </div>
          )}
        </ResizablePanel>

        <ResizableHandle withHandle aria-label="Resize the audience chat panel" />

        <ResizablePanel id="audience-chat" defaultSize="38%" minSize="28%">
          <div className="flex h-full min-h-0 flex-col overflow-hidden">
            <AudienceChatSidebar
              recoveryStatus={chat.recoveryStatus}
              onRetryRecovery={chat.retryRecovery}
              onResetChat={chat.resetChat}
              sessionId={chat.sessionId}
              messages={chat.messages}
              agentActive={chat.agentActive}
              canStop={chat.canStop}
              stopping={chat.stopping}
              stopStatusError={chat.stopStatusError}
              stopStalled={chat.stopStalled}
              onRetryStopStatus={chat.retryStopStatus}
              disabled={!chat.sessionReady}
              budgetExhausted={chat.budgetExhausted}
              onSend={chat.sendMessage}
              onStop={chat.stopTurn}
              prefillValue={chat.prefillValue}
              prefillNonce={chat.prefillNonce}
              pendingBlock={chat.pendingBlock}
              onResolveBlock={chat.resolveBlock}
              blockSubmitting={chat.blockSubmitting}
              composerFocusNonce={chat.composerFocusNonce}
            />
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>

      <RenameAudienceDialog
        open={renameOpen}
        onOpenChange={setRenameOpen}
        title={record?.title ?? ''}
        saving={renameMutation.isPending}
        onSave={onRename}
      />

      <ConfirmDialog
        open={removalPrompt !== null}
        onOpenChange={(open) => {
          if (!open) setRemovalPrompt(null);
        }}
        title="Stop sending these columns?"
        description={
          <>
            This change stops sending{' '}
            {removalPrompt?.map((name, i) => (
              <span key={name}>
                {i > 0 ? ', ' : ''}
                <code className="text-xs">{name}</code>
              </span>
            ))}
            . If a destination maps {removalPrompt?.length === 1 ? 'it' : 'any of them'}, that destination&rsquo;s whole
            sync will fail until you remove the mapping in RudderStack.
          </>
        }
        cancelLabel="Keep them"
        confirmLabel="Save anyway"
        loading={saveMutation.isPending}
        onConfirm={() => {
          confirmedRemovalRef.current = removalPrompt;
          setRemovalPrompt(null);
          onSave();
        }}
      />

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete this audience?"
        description="This permanently deletes the audience. The conversation that created it is kept. This can't be undone."
        cancelLabel="Keep it"
        confirmLabel="Delete"
        loading={deleteMutation.isPending}
        onConfirm={onDelete}
      />

      <SqlModal
        open={sqlOpen}
        onOpenChange={setSqlOpen}
        sql={sqlData?.membershipSql ?? ''}
        title="Audience SQL"
        description="The compiled membership query for this audience."
      />

      {/* Unmounted, not just closed, when the flag is off. Its queries are already `skipToken`-gated
          on `open` and the only way to open it is the (hidden) Activate button, so this is belt-and-
          braces — but it means there is no mounted component that could ever call an `activation.*`
          procedure the server would answer NOT_FOUND for. */}
      {isAudiences && (
        <LazyAudienceActivationSheet
          open={activateOpen}
          onOpenChange={setActivateOpen}
          audienceId={id ?? ''}
          audienceLabel={record?.title ?? 'this audience'}
          audienceIdentifier={record?.definition.expression.idColumn}
          warehouseType={boundRsAccount?.type}
          // `canSelectAccount` used to be threaded here (panel B2): the account step's old
          // `AccountManagement` mount re-derived a stricter admin-only rule than the server's
          // creator-or-admin `isAudienceCreatorOrAdmin`, so a non-admin creator who correctly saw
          // Activate enabled hit a dead Select once inside. The inline `AccountField` that replaced
          // that mount does not gate row selection at all, and `canActivateByRole` below already
          // gates the Activate button with the very same rule — so the prop is gone rather than
          // accepted and ignored. See `AudienceActivationSheetProps`.
        />
      )}

      {isMobile && previewPanel ? (
        <Sheet
          open={previewOpen}
          onOpenChange={(open) => {
            if (!open) setPreviewOpen(false);
          }}
        >
          <SheetContent
            side="bottom"
            showCloseButton={false}
            className="flex h-[85dvh] max-h-[85dvh] w-full flex-col gap-0 p-0"
          >
            <SheetTitle className="sr-only">Audience preview</SheetTitle>
            <SheetDescription className="sr-only">
              Sample of members matching this audience. Close to return to the definition.
            </SheetDescription>
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">{previewPanel}</div>
          </SheetContent>
        </Sheet>
      ) : null}
    </div>
  );
}
